import React from 'react';
import { useTranslation } from 'react-i18next';
import type { FormComponent } from '../../../types/builder';
import { useBuilderStore } from '../../../store/builderStore';
import { ColorPicker } from '../../Common/ColorPicker';
import { ToggleSwitch } from '../../Common/ToggleSwitch';
import { Plus, Trash2 } from 'lucide-react';

interface FormSettingsProps {
  component: FormComponent;
}

const FIELD_TYPES = ['text', 'email', 'tel', 'textarea'] as const;

export const FormSettings: React.FC<FormSettingsProps> = ({ component }) => {
  const { t } = useTranslation('builder');
  const { updateComponent } = useBuilderStore();

  const handleUpdate = (updates: Partial<FormComponent['settings']>) => {
    updateComponent(component.id, {
      settings: { ...component.settings, ...updates },
    });
  };

  const addField = () => {
    const newField = {
      id: `field-${Date.now()}`,
      type: 'text' as const,
      label: t('settings.form.newFieldLabel'),
      placeholder: t('settings.form.newFieldPlaceholder'),
      required: false,
    };
    handleUpdate({ fields: [...component.settings.fields, newField] });
  };

  const removeField = (fieldId: string) => {
    handleUpdate({
      fields: component.settings.fields.filter((f) => f.id !== fieldId),
    });
  };

  const updateField = (fieldId: string, updates: any) => {
    handleUpdate({
      fields: component.settings.fields.map((f) =>
        f.id === fieldId ? { ...f, ...updates } : f
      ),
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium">{t('settings.form.fields')}</label>
          <button
            onClick={addField}
            className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded transition-colors"
          >
            <Plus size={16} />
          </button>
        </div>

        <div className="space-y-3">
          {component.settings.fields.map((field) => (
            <div key={field.id} className="p-3 bg-gray-800 rounded-lg border border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <input
                  type="text"
                  value={field.label}
                  onChange={(e) => updateField(field.id, { label: e.target.value })}
                  className="flex-1 px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <button
                  onClick={() => removeField(field.id)}
                  className="ml-2 p-1 text-red-400 hover:text-red-300"
                >
                  <Trash2 size={14} />
                </button>
              </div>

              <select
                value={field.type}
                onChange={(e) => updateField(field.id, { type: e.target.value })}
                className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-sm mb-3 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {FIELD_TYPES.map((key) => (
                  <option key={key} value={key}>
                    {t(`settings.form.fieldTypes.${key}` as const)}
                  </option>
                ))}
              </select>

              <ToggleSwitch
                label={t('settings.common.required')}
                checked={field.required}
                onChange={(checked) => updateField(field.id, { required: checked })}
              />
            </div>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.submitButtonText')}</label>
        <input
          type="text"
          value={component.settings.submitButtonText}
          onChange={(e) => handleUpdate({ submitButtonText: e.target.value })}
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.buttonColor')}</label>
        <ColorPicker
          color={component.settings.submitButtonColor}
          onChange={(color) => handleUpdate({ submitButtonColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.backgroundColor')}</label>
        <ColorPicker
          color={component.settings.backgroundColor}
          onChange={(color) => handleUpdate({ backgroundColor: color })}
        />
      </div>

      <div>
        <label className="block text-sm font-medium mb-2">{t('settings.common.padding')}</label>
        <input
          type="range"
          min="0"
          max="100"
          value={component.settings.padding}
          onChange={(e) => handleUpdate({ padding: parseInt(e.target.value) })}
          className="w-full"
        />
        <div className="text-xs text-gray-400 mt-1">{component.settings.padding}px</div>
      </div>
    </div>
  );
};
